import { EditOutlined, ReportProblemOutlined } from "@mui/icons-material";
import {
	Box,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Fragment, ReactNode, useState } from "react";
import { BundleAction, BundleDetail } from "../../models/FinishedBundle";
import { formatLength, formatWeight, numCellSx } from "../../utils/utils";
import { BundleForm } from "./BundleForm";
import { HeatGroup, suggestHeatIds } from "./StackerReviewComponents";

type Props = {
	heats: HeatGroup[];
	details: BundleDetail[];
	selectedKey: string | null;
	canEdit: boolean;
	disabled: boolean;
	adding: boolean;
	shiftId?: number;
	shiftHeatIds: number[];
	summary: ReactNode;
	onSelect: (key: string) => void;
	onAddingChange: (adding: boolean) => void;
	onAction: (action: BundleAction) => void;
};

const BundlesBreakdown = ({
	value,
	selected = false,
}: {
	value: string | null;
	selected?: boolean;
}) => {
	const combos = value ? value.split(" · ") : [];
	if (combos.length === 0) return <>-</>;
	const shown = combos.slice(0, 3);
	const rest = combos.slice(3);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
			{shown.map((c) => (
				<Box
					key={c}
					component="span"
					sx={(t) => ({
						display: "inline-flex",
						alignItems: "center",
						height: 22,
						px: 1,
						borderRadius: 11,
						fontSize: 12,
						fontWeight: 700,
						lineHeight: 1,
						flexShrink: 0,
						color: t.palette.text.primary,
						border: `1px solid ${t.palette.grey[300]}`,
						backgroundColor: selected
							? t.palette.grey[300]
							: t.palette.grey[100],
					})}
				>
					{c}
				</Box>
			))}
			{rest.length > 0 && (
				<Tooltip title={rest.join(" · ")}>
					<Box
						component="span"
						sx={(t) => ({
							display: "inline-flex",
							alignItems: "center",
							height: 22,
							px: 0.75,
							borderRadius: 11,
							fontSize: 12,
							fontWeight: 700,
							lineHeight: 1,
							flexShrink: 0,
							color: t.palette.text.secondary,
							border: `1px solid ${t.palette.grey[300]}`,
							backgroundColor: t.palette.background.paper,
						})}
					>
						+{rest.length}
					</Box>
				</Tooltip>
			)}
		</Box>
	);
};

const warningOf = (h: HeatGroup) =>
	[
		h.pending > 0 ? `${h.pending} mill stopped to review` : null,
		h.HeatId == null
			? "check heat: the PLC heat wasn't rolling on Mill #2 at that time"
			: null,
		h.HeatId != null && h.suspicious > 0
			? `${h.suspicious} with the heat to check`
			: null,
	]
		.filter(Boolean)
		.join(" · ");

export const HeatList = ({
	heats,
	details,
	selectedKey,
	canEdit,
	disabled,
	adding,
	shiftId,
	shiftHeatIds,
	summary,
	onSelect,
	onAddingChange,
	onAction,
}: Props) => {
	const [editingKey, setEditingKey] = useState<string | null>(null);
	const columns = canEdit ? 10 : 9;
	const bundlesOf = (key: string) => details.filter((d) => d.GroupKey === key);

	return (
		<Paper
			elevation={2}
			sx={{
				overflow: "hidden",
				flex: "1 1 0",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
			}}
			data-testid="stacker-heat-list"
		>
			<Box
				sx={{
					flexShrink: 0,
					px: 2.5,
					py: 1.5,
					borderBottom: "1px solid",
					borderColor: "divider",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
					gap: 1,
				}}
			>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					Shift heats
				</Typography>
				<Typography
					variant="caption"
					color="text.secondary"
					noWrap
					sx={{ fontSize: "0.71rem" }}
				>
					Select one to see its bundles
				</Typography>
			</Box>

			<TableContainer sx={{ flex: "1 1 auto", minHeight: 0 }}>
				<Table
					size="small"
					stickyHeader
					sx={{
						tableLayout: "fixed",
						"& th, & td": {
							fontSize: "0.83rem",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						},
					}}
				>
					<colgroup>
						{[
							"9%",
							"9%",
							"10%",
							"9%",
							"24%",
							"11%",
							"11%",
							"9%",
							...(canEdit ? [31] : []),
						].map((width, i) => (
							<col key={i} style={{ width }} />
						))}
					</colgroup>
					<TableHead>
						<TableRow sx={{ "& th": { height: 37, py: 0 } }}>
							<TableCell>Section</TableCell>
							<TableCell>Heat</TableCell>
							<TableCell>Grade</TableCell>
							<TableCell>Length</TableCell>
							<TableCell>Bundle × Pieces</TableCell>
							<TableCell sx={numCellSx}>Total Pcs</TableCell>
							<TableCell sx={numCellSx}>Weight (t)</TableCell>
							<TableCell sx={numCellSx}>Scrap</TableCell>
							<TableCell padding="none" />
							{canEdit && <TableCell padding="none" />}
						</TableRow>
					</TableHead>
					<TableBody>
						{heats.map((h) => {
							const selected = h.key === selectedKey;
							const warning = warningOf(h);
							const editRow = h.key === editingKey && (
								<TableRow
									sx={(t) => ({
										"& td": {
											backgroundColor: alpha(t.palette.primary.main, 0.04),
										},
									})}
								>
									<TableCell
										colSpan={columns}
										sx={{ overflow: "visible !important" }}
									>
										<BundleForm
											bundles={bundlesOf(h.key)}
											disabled={disabled}
											shiftId={shiftId}
											shiftHeatIds={shiftHeatIds}
											suggestedHeatIds={suggestHeatIds(
												bundlesOf(h.key),
												details,
											)}
											onSave={(values) => {
												onAction({
													type: "edit",
													ids: bundlesOf(h.key).map((b) => b.BundleId),
													...values,
												});
												setEditingKey(null);
											}}
											onCancel={() => setEditingKey(null)}
										/>
									</TableCell>
								</TableRow>
							);
							return (
								<Fragment key={h.key}>
									<TableRow
										hover
										onClick={() => onSelect(h.key)}
										data-testid={`finished-heat-${h.key}`}
										sx={(t) => ({
											cursor: "pointer",
											"& td": {
												height: 44,
												py: 0,
												fontVariantNumeric: "tabular-nums",
												...(selected
													? {
															backgroundColor: alpha(
																t.palette.primary.main,
																0.08,
															),
														}
													: {}),
											},
											"& td:first-of-type": {
												boxShadow: selected
													? `inset 3px 0 0 ${t.palette.primary.main}`
													: "none",
											},
										})}
									>
										<TableCell>{h.SectionName}</TableCell>
										<TableCell sx={{ fontWeight: 600 }}>
											{h.HeatName || "-"}
										</TableCell>
										<TableCell>{h.GradeName}</TableCell>
										<TableCell>{formatLength(h.LengthInches)}</TableCell>
										<TableCell>
											<BundlesBreakdown
												value={h.breakdown}
												selected={selected}
											/>
										</TableCell>
										<TableCell sx={numCellSx}>
											{h.pieces.toLocaleString()}
										</TableCell>
										<TableCell sx={numCellSx}>
											{formatWeight(h.weight)}
										</TableCell>
										<TableCell sx={numCellSx}>
											{h.scrapPieces ? h.scrapPieces.toLocaleString() : "-"}
										</TableCell>
										<TableCell padding="none" sx={{ textAlign: "center" }}>
											{warning && (
												<Tooltip title={warning}>
													<ReportProblemOutlined
														color="warning"
														sx={{ fontSize: 18, verticalAlign: "middle" }}
													/>
												</Tooltip>
											)}
										</TableCell>
										{canEdit && (
											<TableCell padding="none" sx={{ textAlign: "center" }}>
												<Tooltip title="Edit all the bundles of this row">
													<span>
														<IconButton
															size="small"
															disabled={
																disabled ||
																editingKey !== null ||
																bundlesOf(h.key).length === 0
															}
															onClick={(e) => {
																e.stopPropagation();
																setEditingKey(h.key);
															}}
															aria-label="Edit row"
															data-testid={`finished-heat-edit-${h.key}`}
														>
															<EditOutlined fontSize="small" />
														</IconButton>
													</span>
												</Tooltip>
											</TableCell>
										)}
									</TableRow>
									{editRow}
								</Fragment>
							);
						})}
						{canEdit && adding && (
							<TableRow
								ref={(el) => el?.scrollIntoView({ block: "nearest" })}
								sx={(t) => ({
									"& td": {
										backgroundColor: alpha(t.palette.primary.main, 0.04),
									},
								})}
							>
								<TableCell
									colSpan={columns}
									sx={{ overflow: "visible !important" }}
								>
									<BundleForm
										bundles={[]}
										disabled={disabled}
										shiftId={shiftId}
										shiftHeatIds={shiftHeatIds}
										onSave={({ quantity, ...values }) => {
											onAction({
												type: "create",
												quantity: quantity ?? 1,
												...values,
											});
											onAddingChange(false);
										}}
										onCancel={() => onAddingChange(false)}
									/>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
					{heats.length === 0 && !adding && (
						<TableBody>
							<TableRow>
								<TableCell
									colSpan={columns}
									sx={{ py: 4, textAlign: "center" }}
								>
									<Typography variant="body2" color="text.secondary">
										No bundles recorded for this shift yet.
									</Typography>
								</TableCell>
							</TableRow>
						</TableBody>
					)}
				</Table>
			</TableContainer>

			<Box
				sx={{
					flexShrink: 0,
					borderTop: "1px solid",
					borderColor: "divider",
					display: "flex",
					justifyContent: "flex-end",
					flexWrap: "wrap",
					columnGap: 3,
					rowGap: 0.5,
					px: 2.5,
					py: 1.25,
				}}
			>
				{summary}
			</Box>
		</Paper>
	);
};
